import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { createRef, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Button from '@katalon-studio/katalon-ui/v2/Button';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { AccountInfo } from '.';
import { fromAccounts, fromAuth, fromSubscription, useAppDispatch } from '../../store';
import { LoadingProgress } from '../../layout';
import { useAccount } from '../../account';
import { homePage, useNavigate, useQuery } from '../../routes';
import { checkout, plan, subscriptionManagement } from '../../layout/routes';
import { Account, SubscriptionSource } from '../../models';
import { useParseQueryParam } from './hook/useParseQueryParam';

const useStyle = makeStyles(theme => ({
  backBtn: {
    padding: theme.spacing(0.5, 2),
  },
  continueBtn: {
    border: `1px solid ${theme.refV2.cat.purple[400]} !important`,
    backgroundColor: theme.refV2.cat.purple[400],
    color: theme.common.white,
    padding: theme.spacing(0.5, 2),
    '&:hover': {
      backgroundColor: theme.refV2.cat.purple[800],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.refV2.cat.purple[900],
      color: theme.refV2.cat.purple[400],
    },
  },
}));

export const AccountSelection = () => {
  const classes = useStyle();
  const dispatch = useAppDispatch();
  const { accounts, loading } = useAccount();
  const { navigate, replaceQuery } = useNavigate();
  const { get } = useQuery();
  const route = get('route');
  const spEnabled = get('spEnabled') === 'true';
  const [optedInStarterPackage, setOptedInStarterPackage] = useState(false);
  const accLoading = useSelector(fromAccounts.selectLoading);
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [redirecting, setRedirecting] = useState(false);
  const accountRefs = useRef<any[]>([]);
  const queryParam = useParseQueryParam(selectedAccount);
  const subs = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  accountRefs.current = accounts.map((_, index) => accountRefs.current[index] ?? createRef());

  const onAccountSelect = (account: Account) => {
    const checkStarterPackage = async (accountId: number) => {
      await dispatch(fromAccounts.doCheckStarterPackage({ accountId, spEnabled }))
        .then(unwrapResult)
        .then(isOptedInStarterPackage => {
          setOptedInStarterPackage(isOptedInStarterPackage);
        })
        .catch(() => {
          // do nothing
        });
    };

    checkStarterPackage(account.id)
      .finally(() => setRedirecting(true));
  };

  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedAccount(accounts[0]);
      onAccountSelect(accounts[0]);
    }
    // when there's account is not finish checking permission,
    // we don't want to auto select the account
    if (accountRefs.current.some(ref => ref.current === null || ref.current.loading !== false)) {
      return;
    }

    // filter out the account that is not allowed
    const allowedAccount = accounts
      .filter((_, index) => accountRefs.current[index].current.allowed);

    // if there's only one allowed account, auto select this account
    if (allowedAccount.length === 1) {
      setSelectedAccount(allowedAccount[0]);
      onAccountSelect(allowedAccount[0]);
    }
  }, [accounts, accountRefs]);

  useEffect(() => {
    if (!accLoading && redirecting && selectedAccount) {
      if (optedInStarterPackage) {
        navigate(
          route === checkout.path
            ? `${subscriptionManagement.path}/purchase`
            : subscriptionManagement.path,
          replaceQuery(queryParam),
        );
      }

      navigate(
        route ?? subs.length > 0 ? plan.path : homePage.path,
        replaceQuery(queryParam),
      );
    }
  }, [redirecting, optedInStarterPackage, accLoading]);

  const onBack = () => {
    dispatch(fromAuth.doLogout({
      redirectUrl: `${window.location.origin}/logout`,
    }));
  };

  const onContinue = () => {
    if (selectedAccount) {
      onAccountSelect(selectedAccount);
    }
  };

  return loading ? <LoadingProgress fullscreen /> : (
    <>
      <Box
        id="account-selection-container"
        key="account-selection-container"
        display="flex"
        flexDirection="column"
        alignContent="center"
        alignItems="center"
        justifyContent="center"
        height="100%"
        sx={{
          backgroundColor: theme => theme.palette.grayA600,
        }}
      >
        <Box
          id="account-selection-box"
          key="account-selection-box"
          minWidth={theme => theme.spacing(80)}
          maxWidth={theme => theme.spacing(100)}
          minHeight={theme => theme.spacing(70)}
          maxHeight="90%"
          border="1px solid #D5D8DD"
          borderRadius={theme => theme.borderRadius.radiusRounded4px}
          padding={4}
          sx={{
            backgroundColor: theme => theme.common.white,
          }}
        >
          <Box
            id="account-selection-title"
            key="account-selection-title"
            display="flex"
            flexDirection="column"
            height={theme => theme.spacing(10)}
          >
            <Typography variant="h3" fontWeight={700}>
              <FormattedMessage id="page.account.selection.title" />
            </Typography>
            <Typography variant="body1" paddingY={1} fontSize={14}>
              <FormattedMessage id="page.account.selection.sub.title" />
            </Typography>
          </Box>
          <Box
            id="account-selection-list"
            key="account-selection-list"
            display="flex"
            flexDirection="column"
            height={theme => `calc(100% - ${theme.spacing(20)})`}
            overflow="scroll"
            marginBottom={3}
            marginX={2}
            sx={{
              scrollbarWidth: 'none',
            }}
          >
            <List
              sx={{
                paddingBottom: 2,
              }}
            >
              {accounts.map((account, index) => {
                const selected = selectedAccount?.id === account.id;
                return (
                  <AccountInfo
                    key={`account-selection-account-info-${account.id}`}
                    ref={accountRefs.current[index]}
                    selected={selected}
                    account={account}
                    onSelectAccount={setSelectedAccount}
                  />
                );
              })}
            </List>
          </Box>
          <Divider />
          <Box
            id="account-selection-footer"
            key="account-selection-footer"
            display="flex"
            flexDirection="row"
            alignContent="center"
            alignItems="center"
            justifyContent="space-between"
            height={theme => theme.spacing(10)}
            padding={2}
          >
            <Button
              variant="outlined"
              size="small"
              className={classes.backBtn}
              onClick={onBack}
            >
              <FormattedMessage id="page.account.selection.button.back" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.continueBtn}
              disabled={!selectedAccount}
              onClick={onContinue}
            >
              <FormattedMessage id="page.account.selection.button.continue" />
            </Button>
          </Box>
        </Box>
      </Box>
      {redirecting && (<LoadingProgress fullscreen />)}
    </>
  );
};
